import { FC, useState } from 'react';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import { ReactComponent as WarningIcon } from 'assets/svg/warning-icon-2.svg';
import { ButtonVariants } from 'constants/common';
import { Button, Checkbox, Modal } from 'shared-components';
import { cn } from 'utils/global';

export interface Props {
  warningMessage?: string;
  isOpen: boolean;
  handleClose: VoidFunction;
  handleConfirm: VoidFunction | undefined;
  description?: string | string[] | JSX.Element;
  isLoading?: boolean;
  descriptionClassName?: string;
  isCancelButton?: boolean;
  approveButtonText?: string;
  checkboxLabel?: string;
  isCheckConfirmButton?: boolean;
}
const WarningModal: FC<Props> = ({
  isOpen,
  handleClose,
  handleConfirm,
  description,
  warningMessage,
  isLoading,
  descriptionClassName,
  isCancelButton = true,
  approveButtonText = 'Yes',
  checkboxLabel,
  isCheckConfirmButton,
}) => {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);

  return (
    <Modal dataCy="warning-modal" isOpen={isOpen} isError isDisableOutsideClick hideCross onClose={handleClose}>
      <div className="flex justify-between">
        <div className="flex items-center justify-left">
          <WarningIcon />
          <div className="text-black-900 dark:text-white-00 text-lg ml-3">{warningMessage}</div>
        </div>
        <Button
          dataCy="close-button-icon"
          className="bg-transparent dark:bg-transparent p-0 w-8 h-8 block rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-900 active:bg-neutral-100 dark:active:bg-neutral-900"
          onClick={handleClose}
        >
          <FontAwesomeIcon
            className="w-5 h-5 text-neutral-600 dark:text-neutral-300"
            icon={faClose as IconDefinition}
          />
        </Button>
      </div>
      {description && (
        <div className={cn('text-center mt-4 text-neutral-700 dark:neutral-300 text-bodyMd', descriptionClassName)}>
          {description}
        </div>
      )}

      {isCheckConfirmButton && (
        <div className="block mt-5 text-left">
          <Checkbox
            dataCy="checkbox"
            label={checkboxLabel}
            labelClassName="font-normal"
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            inputClassName="w-full"
          />
        </div>
      )}
      <div className="flex justify-end mt-5 mb-4">
        {isCancelButton && (
          <Button onClick={handleClose} variant={ButtonVariants.OUTLINE} dataCy="warning-modal-cancel-button">
            {t('Cancel')}
          </Button>
        )}
        <Button
          dataCy="warning-modal-confirm-button"
          disabled={!isChecked && isCheckConfirmButton}
          onClick={handleConfirm}
          className="ml-3"
          isLoading={isLoading}
          variant={ButtonVariants.DESTRUCTIVE}
        >
          {t(approveButtonText)}
        </Button>
      </div>
    </Modal>
  );
};

export default WarningModal;
